import { useEffect, useState } from 'react'

import { useToast } from '../../context/ToastContext'
import type { ComicCharacterCard } from '../../lib/comicCreativeBible'
import {
  canSetPanelSceneReferenceUrls,
  type ComicPageReferenceCapContext,
  getPageReferenceSlotUsage,
  pageReferenceCapExceededMessage,
} from '../../lib/comicPageReferenceCap'
import {
  hydratePanelCharacterLines,
  serializeCharacterLinesToDialogue,
} from '../../lib/comicPanelCharacterLines'
import { comicPanelToLightboxItems, lightboxIndexForCandidate } from '../../lib/comicPanelLightbox'
import type { ComicPage, ComicPanel, ComicPanelCharacterLine } from '../../lib/comicTypes'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { ChromeReferenceImagePicker } from '../ChromeReferenceImagePicker'
import { ImageLightbox } from '../ImageLightbox'
import { ThemeModal } from '../modal'
import { ComicPanelCandidateStrip } from './ComicPanelCandidateStrip'
import { ComicPanelCharacterEditor } from './ComicPanelCharacterEditor'

function panelTitle(page: ComicPage | null, panel: ComicPanel): string {
  if (!page) return `分镜 #${panel.order}`
  const slotIdx = page.panelIds.indexOf(panel.id)
  const slot = slotIdx >= 0 ? slotIdx + 1 : panel.order
  return `${page.title} · 第 ${slot} 格`
}

type Props = {
  open: boolean
  angular: boolean
  page: ComicPage | null
  panel: ComicPanel | null
  characters: ComicCharacterCard[]
  saving?: boolean
  referenceCap?: ComicPageReferenceCapContext | null
  onClose: () => void
  onSave: (patch: {
    script: string
    dialogue: string
    narration: string
    referenceImageUrls?: string[]
    characterIds?: string[]
    characterLines?: ComicPanelCharacterLine[]
  }) => void
  onSelectPanelCandidate?: (candidateId: string) => void
}

export function ComicPanelEditModal({
  open,
  angular,
  page,
  panel,
  characters,
  saving = false,
  referenceCap = null,
  onClose,
  onSave,
  onSelectPanelCandidate,
}: Props) {
  const { showToast } = useToast()
  const [draftScript, setDraftScript] = useState('')
  const [draftNarration, setDraftNarration] = useState('')
  const [draftReferenceUrls, setDraftReferenceUrls] = useState<string[]>([])
  const [draftCharacterIds, setDraftCharacterIds] = useState<string[]>([])
  const [draftCharacterLines, setDraftCharacterLines] = useState<ComicPanelCharacterLine[]>([])
  const [expandedCharacterId, setExpandedCharacterId] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const lightboxItems = comicPanelToLightboxItems(panel)

  const hasCharacterCards = characters.length > 0

  const usage =
    referenceCap && referenceCap.cap > 0
      ? getPageReferenceSlotUsage(
          referenceCap.creative,
          referenceCap.pagePanels,
          referenceCap.cap,
        )
      : null

  const sceneHint =
    referenceCap && referenceCap.cap > 0
      ? usage && usage.remaining > 0
        ? `描述本格场景/构图/光影。本页生图最多 ${referenceCap.cap} 张参考图（场景+出场角色造型），当前还可占 ${usage.remaining} 张。`
        : `描述本格场景/构图/光影。本页参考图已满（上限 ${referenceCap.cap} 张），请先去掉其它格场景图或其它出场角色的造型图。`
      : referenceCap && referenceCap.cap === 0
        ? '当前模型不支持参考图，场景参考不会参与生图。'
        : '描述本格场景/构图/光影；与角色造型参考图叠加使用，互不替代。'

  useEffect(() => {
    if (!open || !panel) return
    setDraftScript(panel.script)
    setDraftNarration(panel.narration ?? '')
    setDraftReferenceUrls(panel.referenceImageUrls ?? [])
    const ids = panel.characterIds ?? []
    setDraftCharacterIds(ids)
    const hydrated = hydratePanelCharacterLines(panel)
    setDraftCharacterLines(hydrated)
    setExpandedCharacterId(ids[0] ?? null)
  }, [open, panel])

  const applySceneReferenceUrls = (urls: string[]) => {
    if (!panel || !referenceCap) {
      setDraftReferenceUrls(urls)
      return
    }
    const check = canSetPanelSceneReferenceUrls(
      referenceCap.creative,
      referenceCap.pagePanels,
      panel.id,
      urls,
      referenceCap.cap,
    )
    if (!check.ok) {
      showToast({ message: check.message, variant: 'warning' })
      return
    }
    setDraftReferenceUrls(urls)
  }

  const footer = (
    <div className="flex w-full justify-end gap-2">
      <ChromeToolbarButton type="button" onClick={onClose} disabled={saving}>
        取消
      </ChromeToolbarButton>
      <ChromePrimaryButton
        density="compact"
        type="button"
        disabled={saving}
        onClick={() => {
          const hasCast = draftCharacterIds.length > 0
          onSave({
            script: draftScript,
            dialogue:
              hasCast && hasCharacterCards
                ? serializeCharacterLinesToDialogue(draftCharacterLines, characters)
                : '',
            narration: draftNarration,
            referenceImageUrls: draftReferenceUrls.length ? draftReferenceUrls : undefined,
            characterIds: hasCast ? draftCharacterIds : undefined,
            characterLines:
              hasCast && hasCharacterCards
                ? draftCharacterLines.filter((l) => draftCharacterIds.includes(l.characterId))
                : undefined,
          })
        }}
      >
        {saving ? '保存中…' : '保存'}
      </ChromePrimaryButton>
    </div>
  )

  if (!panel) {
    return (
      <ThemeModal open={open} onClose={onClose} title="编辑分镜" angular={angular} size="md" footer={footer}>
        {null}
      </ThemeModal>
    )
  }

  const sceneAtPageLimit = Boolean(usage && usage.remaining <= 0 && draftReferenceUrls.length === 0)

  return (
    <>
      <ThemeModal
        open={open}
        onClose={onClose}
        title={panelTitle(page, panel)}
        angular={angular}
        size="lg"
        bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
        footer={footer}
      >
        <div className="space-y-4">
          <section className="space-y-2">
            <p className="text-sm font-semibold text-ink">本格场景参考图</p>
            <ChromeReferenceImagePicker
              angular={angular}
              mode="single"
              urls={draftReferenceUrls}
              onChange={applySceneReferenceUrls}
              maxCount={1}
              disabled={sceneAtPageLimit}
              hint={sceneHint}
              emptyPlaceholder="点击从图库选择"
              pickerTitle="选择本格场景参考图"
              atLimitMessage={
                referenceCap && referenceCap.cap > 0
                  ? pageReferenceCapExceededMessage(referenceCap.cap)
                  : undefined
              }
            />
          </section>

          <section className="space-y-3 border-t border-line pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-soft">环境</p>
            <label className="block text-sm font-semibold text-ink">
              画面描述
              <ChromeComposerTextarea
                angular={angular}
                variant="form"
                value={draftScript}
                onChange={(e) => setDraftScript(e.target.value)}
                placeholder="例如：旧码头、浓雾、广角构图"
                maxLength={2000}
                textareaClassName="min-h-[5.5rem]"
              />
            </label>
            <label className="block text-sm font-semibold text-ink">
              旁白
              <ChromeComposerTextarea
                angular={angular}
                variant="form"
                value={draftNarration}
                onChange={(e) => setDraftNarration(e.target.value)}
                placeholder="第三人称叙述；勿与角色心声混用"
                maxLength={500}
              />
            </label>
          </section>

          {hasCharacterCards ? (
            <section className="space-y-2 border-t border-line pt-4">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink-soft">角色</p>
              <p className="text-sm font-semibold text-ink">出场角色</p>
              <ComicPanelCharacterEditor
                angular={angular}
                characters={characters}
                selectedIds={draftCharacterIds}
                lines={draftCharacterLines}
                expandedCharacterId={expandedCharacterId}
                onChangeSelectedIds={setDraftCharacterIds}
                onChangeLines={setDraftCharacterLines}
                onExpandCharacter={setExpandedCharacterId}
              />
            </section>
          ) : null}

          {panel.candidates.length > 0 ? (
            <section className="space-y-2 border-t border-line pt-4">
              <p className="text-sm font-semibold text-ink">候选图</p>
              <ComicPanelCandidateStrip
                angular={angular}
                panel={panel}
                onSelectCandidate={(candidateId) => onSelectPanelCandidate?.(candidateId)}
                onPreviewCandidate={(candidateId) => {
                  const cand = panel.candidates.find((c) => c.id === candidateId)
                  if (!cand) return
                  const idx = lightboxIndexForCandidate(panel, cand.id)
                  if (idx >= 0) setLightboxIndex(idx)
                }}
              />
            </section>
          ) : null}
        </div>
      </ThemeModal>

      <ImageLightbox
        open={lightboxIndex != null}
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </>
  )
}
